import React, { useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Pencil } from 'lucide-react'
import { JobApplication } from './ApplicationWizard'
import { ContactsSection } from './ContactsSection'
import { ResumeFitAnalysis, ResumeSelection, TailoredResumeResponse } from '../services/jobApplicationService'
import { ResumeItem } from '../services/apiService'
import { ResumeSelectionSidebar } from './ResumeSelectionSidebar'
import { AssignedResumeViewer } from './AssignedResumeViewer'
import { PopupTooltip } from './PopupTooltip'
import './ComprehensiveJobDisplay.css'
import { sendDebugLog } from '../utils/debugLogging'

interface ComprehensiveJobDisplayProps {
  jobData: Partial<JobApplication> & { error?: unknown; message?: unknown }
  onDataChange: (field: keyof JobApplication, value: any) => void
  isProcessing?: boolean
  viewMode?: boolean
  fitAnalysis?: any
  isLoadingFitAnalysis?: boolean
  fitAnalysisError?: string | null
  companyIntelligence?: any
  isLoadingCompanyIntelligence?: boolean
  companyIntelligenceError?: string | null
  onReprocessFitAnalysis?: () => Promise<void>
  isReprocessingFitAnalysis?: boolean
  onFetchCompanyIntelligence?: () => Promise<void>
  isFetchingCompanyIntelligence?: boolean
  onOpenCompanyIntelligenceDetails?: () => void
  availableResumes?: ResumeItem[]
  tailoredResume?: TailoredResumeResponse | null
  onResumeSelectionChange?: (selection: ResumeSelection) => Promise<void>
  onTailoredResumeUpdated?: () => void
  hideResumeSelection?: boolean // If true, hide resume selection and customization
}

export const ComprehensiveJobDisplay: React.FC<ComprehensiveJobDisplayProps> = ({
  jobData,
  onDataChange,
  isProcessing = false,
  viewMode = false,
  fitAnalysis = null,
  isLoadingFitAnalysis = false,
  fitAnalysisError = null,
  companyIntelligence = null,
  isLoadingCompanyIntelligence = false,
   
  companyIntelligenceError: _companyIntelligenceError, // Unused but part of interface
  onReprocessFitAnalysis,
  isReprocessingFitAnalysis = false,
  onFetchCompanyIntelligence,
  isFetchingCompanyIntelligence = false,
  onOpenCompanyIntelligenceDetails,
  availableResumes = [],
  tailoredResume = null,
  onResumeSelectionChange,
  onTailoredResumeUpdated,
  hideResumeSelection = false,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [tempValue, setTempValue] = useState<string>('')
  const [arrayInputValues, setArrayInputValues] = useState<Record<string, string>>({})
  const [arrayInputOpen, setArrayInputOpen] = useState<Record<string, boolean>>({})
  
  useEffect(() => {
    setIsMounted(true)
  }, [])
  const [editingCardFields, setEditingCardFields] = useState<boolean>(false)
  const [cardTempValues, setCardTempValues] = useState<{
    company?: string
    position?: string
    salary?: string
    location?: string
    employmentType?: string
    jobLevel?: string
  }>({})
  const [cardOriginalValues, setCardOriginalValues] = useState<{
    company?: string
    position?: string
    salary?: string
    location?: string
    employmentType?: string
    jobLevel?: string
  }>({})
  const [showAllResumesModal, setShowAllResumesModal] = useState<boolean>(false)
  const [selectedResumeForDetails, setSelectedResumeForDetails] = useState<any>(null)
  const [showCompanyIntelligenceDetails, setShowCompanyIntelligenceDetails] = useState<boolean>(false)
  const [isResumeSelectionSidebarOpen, setIsResumeSelectionSidebarOpen] = useState<boolean>(false)

  const handleCompanyScoreClick = () => {
    if (onOpenCompanyIntelligenceDetails) {
      onOpenCompanyIntelligenceDetails()
      return
    }
    setShowCompanyIntelligenceDetails(true)
  }

  // Helper function to get company score color
  const getCompanyScoreColor = (score: number): string => {
    if (score >= 70) return 'excellent'
    if (score >= 50) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const handleEditStart = (field: string, currentValue: any) => {
    setEditingField(field)
    setTempValue(currentValue || '')
  }

  const handleEditSave = (field: keyof JobApplication) => {
    onDataChange(field, tempValue)
    setEditingField(null)
    setTempValue('')
  }

  const handleEditCancel = () => {
    setEditingField(null)
    setTempValue('')
  }

  const handleCardEditStart = () => {
    const currentCardValues = {
      company: jobData.company || '',
      position: jobData.position || '',
      salary: jobData.salary || '',
      location: jobData.location || '',
      employmentType: jobData.employmentType || '',
      jobLevel: jobData.jobLevel || ''
    }
    setEditingCardFields(true)
    setCardTempValues(currentCardValues)
    setCardOriginalValues(currentCardValues)
  }

  const handleCardEditSave = () => {
    setEditingCardFields(false)
    setCardTempValues({})
    setCardOriginalValues({})
  }

  const handleCardEditCancel = () => {
    Object.entries(cardOriginalValues).forEach(([field, value]) => {
      onDataChange(field as keyof JobApplication, value ?? '')
    })
    setEditingCardFields(false)
    setCardTempValues({})
    setCardOriginalValues({})
  }

  const handleCardFieldChange = (field: string, value: any) => {
    setCardTempValues(prev => ({
      ...prev,
      [field]: value
    }))
    onDataChange(field as keyof JobApplication, value)
  }

  const handleArrayEdit = (field: keyof JobApplication, value: string[]) => {
    onDataChange(field, value)
  }

  const openArrayInput = (field: keyof JobApplication) => {
    setArrayInputOpen(prev => ({ ...prev, [field]: true }))
  }

  const closeArrayInput = (field: keyof JobApplication) => {
    setArrayInputOpen(prev => ({ ...prev, [field]: false }))
    setArrayInputValues(prev => ({ ...prev, [field]: '' }))
  }

  const updateArrayInput = (field: keyof JobApplication, value: string) => {
    setArrayInputValues(prev => ({ ...prev, [field]: value }))
  }

  const handleArrayItemAdd = (field: keyof JobApplication, currentArray: any[]) => {
    const nextValue = (arrayInputValues[field] || '').trim()
    if (!nextValue) {
      return
    }
    handleArrayEdit(field, [...currentArray, nextValue])
    closeArrayInput(field)
  }

  const removeArrayItem = (field: keyof JobApplication, currentArray: string[], index: number) => {
    handleArrayEdit(field, currentArray.filter((_, i) => i !== index))
  }

  // Helper function to check if a field has a meaningful value
  const hasValue = (value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== '' && value.trim().toLowerCase() !== 'not specified'
    if (Array.isArray(value)) return value.length > 0
    return true
  }

  // Extract best matching resume from fit analysis
  const bestMatchResume = useMemo<ResumeFitAnalysis | null>(() => {
    if (!fitAnalysis?.resumeFitAnalyses || fitAnalysis.resumeFitAnalyses.length === 0) {
      return null
    }
    // Sort by fit score descending and return the best match
    const sortedAnalyses = [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)
    const best = sortedAnalyses[0] || null
    return best
  }, [fitAnalysis])

  // Normalize text for better matching (remove special chars, handle variations)
  const normalizeText = (text: string): string => {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
  }

  // Check if an item is explicitly mentioned in gaps
  const checkItemInGaps = (item: string, bestMatch: ResumeFitAnalysis | null): boolean => {
    if (!bestMatch || !bestMatch.gaps || bestMatch.gaps.length === 0) {
      return false
    }

    const itemLower = item.toLowerCase().trim()
    if (!itemLower) return false

    const normalizedItem = normalizeText(itemLower)

    // Check if item appears in gaps
    return bestMatch.gaps.some(gap => {
      const normalizedGap = normalizeText(gap)
      return normalizedGap.includes(normalizedItem) || 
             normalizedItem.includes(normalizedGap)
    })
  }

  // Check if an item matches based on resume fit analysis
  const checkItemMatch = (item: string, bestMatch: ResumeFitAnalysis | null): boolean => {
    if (!bestMatch) {
      return false
    }

    const itemLower = item.toLowerCase().trim()
    if (!itemLower) return false

    const normalizedItem = normalizeText(itemLower)

    // Check if item appears in gaps (negative match - exclude if mentioned as gap)
    const inGaps = checkItemInGaps(item, bestMatch)

    // If item is in gaps, it's definitely not a match
    if (inGaps) {
      return false
    }

    // If strengths array exists and has items, check against strengths
    if (bestMatch.strengths && bestMatch.strengths.length > 0) {
      const inStrengths = bestMatch.strengths.some(strength => {
        const normalizedStrength = normalizeText(strength)
        // Check for exact match or substring match
        return normalizedStrength.includes(normalizedItem) || 
               normalizedItem.includes(normalizedStrength) ||
               // Check for word boundary matches (e.g., "React" matches "React.js" or "ReactJS")
               normalizedStrength.split(/\s+/).some(word => 
                 word === normalizedItem || 
                 normalizedItem === word ||
                 word.includes(normalizedItem) ||
                 normalizedItem.includes(word)
               )
      })
      return inStrengths
    }

    // If strengths is empty, we can only rely on match percentages
    // If all match percentages are 0, nothing matches - return false immediately
    const skillsMatch = bestMatch.skillsMatch || 0
    const experienceMatch = bestMatch.experienceMatch || 0
    const educationMatch = bestMatch.educationMatch || 0
    
    // If all match percentages are 0, return false (no matches at all)
    if (skillsMatch === 0 && experienceMatch === 0 && educationMatch === 0) {
      return false
    }

    // If strengths is empty but we have some match percentage > 0, we can't reliably determine
    // which specific items match without the strengths array. The reasoning fields might mention
    // items but they could be mentioned in a negative context (e.g., "Lack of X").
    // So we return false - only show checkmarks when strengths array explicitly lists matches.
    return false
  }

  const EditableField: React.FC<{
    field: string
    value: any
    label: string
    type?: 'text' | 'textarea' | 'select'
    options?: string[]
    icon?: string
    badge?: string
    required?: boolean
    hideIfEmpty?: boolean
    fullWidth?: boolean
    readOnly?: boolean
  }> = ({ field, value, label, type = 'text', options, icon, badge, required = false, hideIfEmpty = true, fullWidth = false, readOnly = false }) => {
    const isEditing = editingField === field
    
    // Hide field if empty and hideIfEmpty is true (default behavior)
    if (hideIfEmpty && !required && !hasValue(value) && !isEditing) {
      return null
    }
    
    const displayValue = value || 'Not specified'

    const renderAddItemControl = (currentArray: any[]) => {
      if (viewMode || readOnly) return null
      const fieldKey = field as keyof JobApplication
      const isOpen = arrayInputOpen[fieldKey]
      const inputValue = arrayInputValues[fieldKey] || ''

      if (isOpen) {
        return (
          <div className="add-item-input-row">
            <input
              className="add-item-input"
              type="text"
              value={inputValue}
              onChange={(e) => updateArrayInput(fieldKey, e.target.value)}
              placeholder={`Add ${label.toLowerCase()}`}
              aria-label={`Add ${label}`}
            />
            <button
              className="add-item-confirm"
              onClick={() => handleArrayItemAdd(fieldKey, currentArray)}
              title="Add item"
            >
              Add
            </button>
            <button
              className="add-item-cancel"
              onClick={() => closeArrayInput(fieldKey)}
              title="Cancel"
            >
              Cancel
            </button>
          </div>
        )
      }

      return (
        <button
          className="add-item-btn"
          onClick={() => openArrayInput(fieldKey)}
          title={`Add ${label.toLowerCase()}`}
        >
          + Add {label.toLowerCase()}
        </button>
      )
    }

    return (
      <div className={`field-card ${fullWidth ? 'field-card-full-width' : ''}`} data-field={field}>
        <div className="field-header">
          <div className="field-label">
            {icon && <span className="field-icon">{icon}</span>}
            <span className="label-text">{label}</span>
            {required && <span className="required-indicator">*</span>}
            {badge && <span className="field-badge">{badge}</span>}
          </div>
          {!viewMode && !readOnly && (
            <button 
              className="edit-btn"
              onClick={() => handleEditStart(field, value)}
              title="Edit"
            >
              ✏️
            </button>
          )}
        </div>
        
        <div className="field-content">
          {isEditing ? (
            <div className="edit-mode">
              {(() => {
                if (type === 'textarea') {
                  return (
                    <textarea
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      className="edit-textarea"
                      rows={3}
                      aria-label={`Edit ${label}`}
                    />
                  )
                }
                if (type === 'select') {
                  return (
                    <select
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      className="edit-select"
                      aria-label={`Select ${label}`}
                    >
                      <option value="">Select {label.toLowerCase()}</option>
                      {options?.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )
                }
                return (
                  <input
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    className="edit-input"
                    aria-label={`Edit ${label}`}
                  />
                )
              })()}
              <div className="edit-actions">
                <button 
                  className="save-btn"
                  onClick={() => handleEditSave(field as keyof JobApplication)}
                  title="Save changes"
                >
                  ✓
                </button>
                <button 
                  className="cancel-btn"
                  onClick={handleEditCancel}
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="display-value">
              {Array.isArray(value) ? (
                <div className="array-display">
                  {(() => {
                    if (value.length > 0) {
                      // Special handling for responsibilities - show as bulleted list
                      if (field === 'responsibilities') {
                        return (
                          <>
                            <ul className="responsibilities-list">
                              {value.map((item, index) => {
                                const itemText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                const isMatched = bestMatchResume && checkItemMatch(itemText, bestMatchResume)
                                const isInGaps = bestMatchResume && checkItemInGaps(itemText, bestMatchResume)
                                const showRedX = bestMatchResume && !isMatched && (isInGaps || (bestMatchResume.experienceMatch === 0 && bestMatchResume.skillsMatch === 0 && bestMatchResume.educationMatch === 0))
                                return (
                                  <li key={`${item}-${index}`} className="responsibility-item">
                                    <span className="responsibility-text">
                                      {itemText}
                                    </span>
                                    {isMatched && (
                                      <span className="requirement-match-checkmark" title="This requirement matches your resume">
                                        ✓
                                      </span>
                                    )}
                                    {showRedX && (
                                      <span className="requirement-mismatch-x" title="This requirement does not match your resume">
                                        ✗
                                      </span>
                                    )}
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Special handling for technologies - show as interactive bulleted list (similar to responsibilities)
                      if (field === 'technologies') {
                        return (
                          <>
                            <ul className="technologies-list">
                              {value.map((item, index) => {
                                const displayText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                const isMatched = bestMatchResume && checkItemMatch(displayText, bestMatchResume)
                                const isInGaps = bestMatchResume && checkItemInGaps(displayText, bestMatchResume)
                                const showRedX = bestMatchResume && !isMatched && (isInGaps || (bestMatchResume.skillsMatch === 0 && bestMatchResume.experienceMatch === 0 && bestMatchResume.educationMatch === 0))
                                return (
                                  <li key={`${item}-${index}`} className="technology-item">
                                    <span className="technology-icon" aria-hidden="true">⚙️</span>
                                    <span className="technology-text">
                                      {displayText}
                                    </span>
                                    {isMatched && (
                                      <span className="requirement-match-checkmark" title="This technology matches your resume">
                                        ✓
                                      </span>
                                    )}
                                    {showRedX && (
                                      <span className="requirement-mismatch-x" title="This technology does not match your resume">
                                        ✗
                                      </span>
                                    )}
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Special handling for required qualifications - show as bulleted list
                      if (field === 'requiredQualifications') {
                        return (
                          <>
                            <ul className="qualifications-list qualifications-required">
                              {value.map((item, index) => {
                                const itemText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                const isMatched = bestMatchResume && checkItemMatch(itemText, bestMatchResume)
                                const isInGaps = bestMatchResume && checkItemInGaps(itemText, bestMatchResume)
                                const showRedX = bestMatchResume && !isMatched && (isInGaps || (bestMatchResume.experienceMatch === 0 && bestMatchResume.skillsMatch === 0 && bestMatchResume.educationMatch === 0))
                                return (
                                  <li key={`${item}-${index}`} className="qualification-item">
                                    <span className="qualification-text">
                                      {itemText}
                                    </span>
                                    {isMatched && (
                                      <span className="requirement-match-checkmark" title="This qualification matches your resume">
                                        ✓
                                      </span>
                                    )}
                                    {showRedX && (
                                      <span className="requirement-mismatch-x" title="This qualification does not match your resume">
                                        ✗
                                      </span>
                                    )}
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Special handling for optional qualifications - show as bulleted list
                      if (field === 'optionalQualifications') {
                        return (
                          <>
                            <ul className="qualifications-list qualifications-optional">
                              {value.map((item, index) => {
                                const itemText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                const isMatched = bestMatchResume && checkItemMatch(itemText, bestMatchResume)
                                const isInGaps = bestMatchResume && checkItemInGaps(itemText, bestMatchResume)
                                const showRedX = bestMatchResume && !isMatched && (isInGaps || (bestMatchResume.experienceMatch === 0 && bestMatchResume.skillsMatch === 0 && bestMatchResume.educationMatch === 0))
                                return (
                                  <li key={`${item}-${index}`} className="qualification-item">
                                    <span className="qualification-text">
                                      {itemText}
                                    </span>
                                    {isMatched && (
                                      <span className="requirement-match-checkmark" title="This qualification matches your resume">
                                        ✓
                                      </span>
                                    )}
                                    {showRedX && (
                                      <span className="requirement-mismatch-x" title="This qualification does not match your resume">
                                        ✗
                                      </span>
                                    )}
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Special handling for education - clean list format
                      if (field === 'education') {
                        return (
                          <>
                            <ul className="education-list">
                              {value.map((item, index) => {
                                const itemText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                const isMatched = bestMatchResume && checkItemMatch(itemText, bestMatchResume)
                                const isInGaps = bestMatchResume && checkItemInGaps(itemText, bestMatchResume)
                                const showRedX = bestMatchResume && !isMatched && (isInGaps || (bestMatchResume.educationMatch === 0 && bestMatchResume.skillsMatch === 0 && bestMatchResume.experienceMatch === 0))
                                return (
                                  <li key={`edu-${index}`} className="education-item">
                                    <span className="education-icon" aria-hidden="true">🎓</span>
                                    <span className="education-text">
                                      {itemText}
                                    </span>
                                    {isMatched && (
                                      <span className="requirement-match-checkmark" title="This education requirement matches your resume">
                                        ✓
                                      </span>
                                    )}
                                    {showRedX && (
                                      <span className="requirement-mismatch-x" title="This education requirement does not match your resume">
                                        ✗
                                      </span>
                                    )}
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Special handling for benefits - show as interactive bulleted list (similar to technologies)
                      if (field === 'benefits') {
                        return (
                          <>
                            <ul className="benefits-list">
                              {value.map((item, index) => {
                                const displayText = typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : String(item)
                                return (
                                  <li key={`${item}-${index}`} className="benefit-item">
                                    <span className="benefit-icon" aria-hidden="true">🎁</span>
                                    <span className="benefit-text">
                                      {displayText}
                                    </span>
                                    {!viewMode && !readOnly && (
                                      <button 
                                        className="remove-item-inline"
                                        onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                            {renderAddItemControl(value)}
                          </>
                        )
                      }
                      
                      // Default array display for other fields
                      return (
                        <>
                          <div className="array-items">
                            {value.map((item, index) => (
                              <span key={`${item}-${index}`} className="array-item">
                                {typeof item === 'object' && item !== null && 'degree' in item && 'field' in item 
                                  ? `${item.degree || 'Degree'} in ${item.field || 'Field'}${item.requirement ? ` (${item.requirement})` : ''}`
                                  : item}
                                {!viewMode && !readOnly && (
                                  <button 
                                    className="remove-item"
                                    onClick={() => removeArrayItem(field as keyof JobApplication, value, index)}
                                    title="Remove item"
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                          {renderAddItemControl(value)}
                        </>
                      )
                    }
                    // Empty array - only show if editing or not hiding empty fields
                    if (!isEditing && hideIfEmpty) return null
                    return (
                      <>
                        <span className="empty-array">No items specified</span>
                        {renderAddItemControl(value)}
                      </>
                    )
                  })()}
                </div>
              ) : (
                <span className="text-value">{displayValue}</span>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (isProcessing) {
    return (
      <div className="comprehensive-display processing">
        <div className="processing-overlay">
          <div className="processing-spinner"></div>
          <h2>Analyzing Job Posting...</h2>
          <p>This usually takes about 30-60 seconds.</p>
        </div>
      </div>
    )
  }

  // Check if we have an error response from the service
  const hasError = jobData && typeof jobData === 'object' && 'error' in jobData

  return (
    <div className="comprehensive-display">
      {hasError && (
        <div className="error-banner">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <h3>Service Unavailable</h3>
            <p>{String(jobData.error || 'Unknown error')}</p>
            <p className="error-message">{String(jobData.message || 'No additional details')}</p>
          </div>
        </div>
      )}

      <div className="display-grid-horizontal">
        {/* Section 1: Basic Information - Combined Main Info Card */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            {/* Combined Main Info Card: Company, Position Title, Salary */}
            <div className={`field-card combined-field-card${!viewMode ? ' combined-field-card--with-actions' : ''}`}>
              {!viewMode && !editingCardFields && (
                <div className="card-edit-button-container">
                  <PopupTooltip content="Edit all fields in this card (company, position, salary, location, type, level, specializations)" placement="left">
                    <button
                      type="button"
                      className="card-edit-icon-button"
                      onClick={handleCardEditStart}
                      aria-label="Edit all fields"
                    >
                      <Pencil className="card-edit-icon" size={16} />
                    </button>
                  </PopupTooltip>
                </div>
              )}
              {!viewMode && editingCardFields && (
                <div className="card-edit-actions-container">
                  <button 
                    className="card-save-button"
                    onClick={handleCardEditSave}
                    title="Save all changes"
                  >
                    ✓ Save All
                  </button>
                  <button 
                    className="card-cancel-button"
                    onClick={handleCardEditCancel}
                    title="Cancel editing"
                  >
                    ✕ Cancel
                  </button>
                </div>
              )}
              <div className="combined-field-header main-info-header">
                {/* Company */}
                <div className="main-info-item">
                  <div className="main-info-label">
                    <span className="field-icon">🏢</span>
                    <span className="label-text">Company</span>
                  </div>
                  <div className="main-info-value">
                    {editingCardFields ? (
                      <input
                        type="text"
                        value={cardTempValues.company || ''}
                        onChange={(e) => handleCardFieldChange('company', e.target.value)}
                        className="edit-input-inline-header"
                        aria-label="Edit Company"
                      />
                    ) : editingField === 'company' ? (
                      <div className="edit-mode-inline-header">
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          className="edit-input-inline-header"
                          aria-label="Edit Company"
                        />
                        <div className="edit-actions-inline">
                          <button 
                            className="save-btn"
                            onClick={() => handleEditSave('company')}
                            title="Save changes"
                          >
                            ✓
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={handleEditCancel}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="header-value-container flex items-center gap-2">
                        {/* Company Score Badge - Show before company name */}
                        {isLoadingCompanyIntelligence || isFetchingCompanyIntelligence ? (
                          <div className="company-score-loading-spinner" title="Loading company intelligence...">
                            <div className="spinner-small"></div>
                          </div>
                        ) : companyIntelligence?.status === 'ok' && companyIntelligence?.overall_score !== undefined ? (
                          <div
                            className={`fit-score-badge-large company-score-badge-inline company-score-badge-clickable ${getCompanyScoreColor(companyIntelligence.overall_score)} cursor-pointer flex-shrink-0`}
                            onClick={handleCompanyScoreClick}
                          >
                            <div className="fit-score-badge-percentage">{companyIntelligence.overall_score}</div>
                            <div className="fit-score-badge-hint">Click for more</div>
                          </div>
                        ) : null}
                        <span className="header-value-text">{jobData.company || 'Not specified'}</span>
                        {/* Company Intelligence Button */}
                        {jobData.company && onFetchCompanyIntelligence && (
                          <button
                            className={`fetch-company-intelligence-button ${isLoadingCompanyIntelligence || isFetchingCompanyIntelligence ? 'disabled' : ''}`}
                            onClick={onFetchCompanyIntelligence}
                            disabled={isLoadingCompanyIntelligence || isFetchingCompanyIntelligence}
                            title={companyIntelligence ? 'Refresh Company Intelligence' : 'Generate Company Intelligence'}
                          >
                            {isLoadingCompanyIntelligence || isFetchingCompanyIntelligence ? '⏳' : '🏢'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Position Title */}
                <div className="main-info-item">
                  <div className="main-info-label">
                    <span className="field-icon">💼</span>
                    <span className="label-text">Position Title</span>
                  </div>
                  <div className="main-info-value">
                    {editingCardFields ? (
                      <input
                        type="text"
                        value={cardTempValues.position || ''}
                        onChange={(e) => handleCardFieldChange('position', e.target.value)}
                        className="edit-input-inline-header"
                        aria-label="Edit Position Title"
                      />
                    ) : editingField === 'position' ? (
                      <div className="edit-mode-inline-header">
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          className="edit-input-inline-header"
                          aria-label="Edit Position Title"
                        />
                        <div className="edit-actions-inline">
                          <button 
                            className="save-btn"
                            onClick={() => handleEditSave('position')}
                            title="Save changes"
                          >
                            ✓
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={handleEditCancel}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="header-value-container">
                        <span className="header-value-text">{jobData.position || 'Not specified'}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Salary */}
                <div className="main-info-item">
                  <div className="main-info-label">
                    <span className="field-icon">💰</span>
                    <span className="label-text">Salary</span>
                  </div>
                  <div className="main-info-value">
                    {editingCardFields ? (
                      <input
                        type="text"
                        value={cardTempValues.salary || ''}
                        onChange={(e) => handleCardFieldChange('salary', e.target.value)}
                        className="edit-input-inline-header"
                        aria-label="Edit Salary"
                      />
                    ) : editingField === 'salary' ? (
                      <div className="edit-mode-inline-header">
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          className="edit-input-inline-header"
                          aria-label="Edit Salary"
                        />
                        <div className="edit-actions-inline">
                          <button 
                            className="save-btn"
                            onClick={() => handleEditSave('salary')}
                            title="Save changes"
                          >
                            ✓
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={handleEditCancel}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="header-value-container">
                        <span className="header-value-text">{jobData.salary || 'Not specified'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="combined-field-content">
                {/* Additional Info Row: Location, Type, Job Level, Specializations */}
                <div className="combined-field-row multi-field-row">
                  
                  {/* Location */}
                  <div className="combined-field-item-inline">
                    <div className="combined-field-label-inline">
                      <span className="field-icon-small">📍</span>
                      <span>Location:</span>
                    </div>
                    <div className="combined-field-value-inline">
                      {editingCardFields ? (
                        <input
                          type="text"
                          value={cardTempValues.location || ''}
                          onChange={(e) => handleCardFieldChange('location', e.target.value)}
                          className="edit-input-inline"
                          aria-label="Edit Location"
                        />
                      ) : editingField === 'location' ? (
                        <div className="edit-mode-inline">
                          <input
                            type="text"
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            className="edit-input-inline"
                            aria-label="Edit Location"
                          />
                          <div className="edit-actions-inline">
                            <button 
                              className="save-btn"
                              onClick={() => handleEditSave('location')}
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={handleEditCancel}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-value-container">
                          <span className="text-value-inline">{jobData.location || 'Not specified'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Employment Type */}
                  <div className="combined-field-item-inline">
                    <div className="combined-field-label-inline">
                      <span className="field-icon-small">⏰</span>
                      <span>Type:</span>
                    </div>
                    <div className="combined-field-value-inline">
                      {editingCardFields ? (
                        <select
                          value={cardTempValues.employmentType || ''}
                          onChange={(e) => handleCardFieldChange('employmentType', e.target.value)}
                          className="edit-select-inline"
                          aria-label="Edit Employment Type"
                        >
                          <option value="">Select type</option>
                          {['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship', 'Volunteer', 'Other'].map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : editingField === 'employmentType' ? (
                        <div className="edit-mode-inline">
                          <select
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            className="edit-select-inline"
                            aria-label="Edit Employment Type"
                          >
                            <option value="">Select type</option>
                            {['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship', 'Volunteer', 'Other'].map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                          <div className="edit-actions-inline">
                            <button 
                              className="save-btn"
                              onClick={() => handleEditSave('employmentType')}
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={handleEditCancel}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-value-container">
                          <span className="text-value-inline">{jobData.employmentType || 'Not specified'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Job Level */}
                  <div className="combined-field-item-inline">
                    <div className="combined-field-label-inline">
                      <span className="field-icon-small">📊</span>
                      <span>Job Level:</span>
                    </div>
                    <div className="combined-field-value-inline">
                      {editingCardFields ? (
                        <select
                          value={cardTempValues.jobLevel || ''}
                          onChange={(e) => handleCardFieldChange('jobLevel', e.target.value)}
                          className="edit-select-inline"
                          aria-label="Edit Job Level"
                        >
                          <option value="">Select job level</option>
                          {['Junior', 'Mid-level', 'Senior', 'Lead', 'Manager', 'Director', 'VP', 'C-level'].map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : editingField === 'jobLevel' ? (
                        <div className="edit-mode-inline">
                          <select
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            className="edit-select-inline"
                            aria-label="Edit Job Level"
                          >
                            <option value="">Select job level</option>
                            {['Junior', 'Mid-level', 'Senior', 'Lead', 'Manager', 'Director', 'VP', 'C-level'].map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                          <div className="edit-actions-inline">
                            <button 
                              className="save-btn"
                              onClick={() => handleEditSave('jobLevel')}
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={handleEditCancel}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-value-container">
                          <span className="text-value-inline">{jobData.jobLevel || 'Not specified'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Position Specializations */}
                  <div className="combined-field-item-inline">
                    <div className="combined-field-label-inline">
                      <span className="field-icon-small">🏷️</span>
                      <span>Specializations:</span>
                    </div>
                    <div className="combined-field-value-inline">
                      {editingField === 'positionSpecialization' ? (
                        <div className="edit-mode-inline">
                          <input
                            type="text"
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            className="edit-input-inline"
                            placeholder="Enter specialization, press Enter"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                const currentArray = jobData.positionSpecialization || []
                                const newItem = tempValue.trim()
                                if (newItem) {
                                  handleArrayEdit('positionSpecialization', [...currentArray, newItem])
                                  setTempValue('')
                                }
                              }
                            }}
                            aria-label="Edit Position Specializations"
                          />
                          <div className="edit-actions-inline">
                            <button 
                              className="save-btn"
                              onClick={() => {
                                setEditingField(null)
                                setTempValue('')
                              }}
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={handleEditCancel}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-value-container">
                          <div className="array-display-compact">
                            {(jobData.positionSpecialization && jobData.positionSpecialization.length > 0) ? (
                              <div className="array-items-compact">
                                {jobData.positionSpecialization.map((item: string, index: number) => (
                                  <span key={`specialization-${index}`} className="array-item-compact">
                                    {item}
                                    {!viewMode && (
                                      <button 
                                        className="remove-item-compact"
                                        onClick={() => removeArrayItem('positionSpecialization', jobData.positionSpecialization || [], index)}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="empty-array-compact">None</span>
                            )}
                          </div>
                          {!viewMode && (
                            <button 
                              className="edit-btn-inline-small"
                              onClick={() => handleEditStart('positionSpecialization', jobData.positionSpecialization)}
                              title="Edit Specializations"
                            >
                              {jobData.positionSpecialization && jobData.positionSpecialization.length > 0 ? '✏️' : '+ Add'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Job Fitment Score - Hidden in edit mode */}
        {!hideResumeSelection && (fitAnalysis || isLoadingFitAnalysis || fitAnalysisError) && (
          <div className="section-group-horizontal fit-score-section scores-header">
            <div className="fields-grid-compact scores-container">
              {/* Resume Fit Score */}
              {(fitAnalysis || isLoadingFitAnalysis || fitAnalysisError) && (
                <div className="score-card fit-score-card">
                  {(() => {
                    if (isLoadingFitAnalysis) {
                      return (
                        <div className="fit-score-loading">
                          <div className="spinner-small"></div>
                          <p>Analyzing resume fit... This usually takes about 30-60 seconds.</p>
                        </div>
                      )
                    }
                    if (fitAnalysisError) {
                      return (
                        <div className="fit-score-error">
                          <p>⚠️ {fitAnalysisError}</p>
                        </div>
                      )
                    }
                    if (fitAnalysis?.resumeFitAnalyses && fitAnalysis.resumeFitAnalyses.length > 0) {
                      // Sort by fit score descending to show best match first
                      const sortedAnalyses = [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)
                      const bestMatch = sortedAnalyses[0]
                      
                      const getFitScoreColor = (score: number): string => {
                        if (score >= 80) return 'excellent'
                        if (score >= 60) return 'good'
                        if (score >= 40) return 'moderate'
                        return 'low'
                      }
                      const getFitScoreLabel = (score: number): string => {
                        if (score >= 80) return 'Excellent Match'
                        if (score >= 60) return 'Good Match'
                        if (score >= 40) return 'Moderate Match'
                        return 'Low Match'
                      }
                      
                      return (
                        <>
                    <div className="fit-score-display-compact">
                      {/* Best Match Summary - Compact Layout */}
                      <div className="fit-score-best-match-compact">
                        <div className="fit-score-compact-header">
                          <span className="fit-score-compact-label">Best Match</span>
                          <div 
                            className={`fit-score-badge-large fit-score-badge-compact fit-score-badge-clickable ${getFitScoreColor(bestMatch.fitScore)} cursor-pointer`}
                            onClick={() => setSelectedResumeForDetails(bestMatch)}
                          >
                            <div className="fit-score-badge-percentage">{bestMatch.fitScore}%</div>
                            <div className="fit-score-badge-hint">Click for more</div>
                          </div>
                        </div>
                        <div className="fit-score-compact-content">
                          <div className="fit-score-compact-name-row">
                            <span className="fit-score-compact-name">{bestMatch.resumeName || 'Resume 1'}</span>
                            <span className="fit-score-compact-label-text">{getFitScoreLabel(bestMatch.fitScore)}</span>
                          </div>
                          <div className="fit-score-compact-metrics">
                            <div 
                              className="fit-metric-compact cursor-pointer"
                              onClick={() => setSelectedResumeForDetails(bestMatch)}
                            >
                              <span className="fit-metric-label-compact">Skills:</span>
                              <span className="fit-metric-value-compact">{bestMatch.skillsMatch}%</span>
                            </div>
                            <div 
                              className="fit-metric-compact cursor-pointer"
                              onClick={() => setSelectedResumeForDetails(bestMatch)}
                            >
                              <span className="fit-metric-label-compact">Experience:</span>
                              <span className="fit-metric-value-compact">{bestMatch.experienceMatch}%</span>
                            </div>
                            <div 
                              className="fit-metric-compact cursor-pointer"
                              onClick={() => setSelectedResumeForDetails(bestMatch)}
                            >
                              <span className="fit-metric-label-compact">Education:</span>
                              <span className="fit-metric-value-compact">{bestMatch.educationMatch}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* All Resumes Button - Only show if 2+ resumes */}
                      {sortedAnalyses.length >= 2 && (
                        <div className="fit-score-all-resumes-btn">
                          <button 
                            className="view-all-resumes-btn"
                            onClick={() => setShowAllResumesModal(true)}
                            title="View all resume matches"
                          >
                            View All Resumes ({sortedAnalyses.length})
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* All Resumes Modal */}
                    {showAllResumesModal && (
                      <div 
                        className="resumes-modal-overlay"
                        onClick={(e) => {
                          if (e.target === e.currentTarget) {
                            setShowAllResumesModal(false)
                          }
                        }}
                      >
                        <div className="resumes-modal">
                          <div className="resumes-modal-header">
                            <h3>All Resume Matches</h3>
                            <button 
                              className="resumes-modal-close"
                              onClick={() => setShowAllResumesModal(false)}
                              aria-label="Close"
                              title="Close"
                            >
                              ✕
                            </button>
                          </div>
                          <div className="resumes-modal-content">
                            <div className="resumes-grid">
                              {sortedAnalyses.map((analysis: any, index: number) => (
                                <div 
                                  key={`fit-${analysis.resumeId}-${index}`} 
                                  className="resume-card-grid cursor-pointer"
                                  onClick={() => setSelectedResumeForDetails(analysis)}
                                >
                                  <div className="resume-card-header-grid">
                                    <div className="resume-card-name-grid">{analysis.resumeName || `Resume ${index + 1}`}</div>
                                    <div className={`resume-card-badge-grid ${getFitScoreColor(analysis.fitScore)}`}>
                                      {analysis.fitScore}%
                                    </div>
                                  </div>
                                  <div className="resume-card-label-grid">{getFitScoreLabel(analysis.fitScore)}</div>
                                  <div className="resume-card-metrics-grid">
                                    <div className="resume-metric-grid">
                                      <span className="resume-metric-label-grid">Skills:</span>
                                      <span className="resume-metric-value-grid">{analysis.skillsMatch}%</span>
                                    </div>
                                    <div className="resume-metric-grid">
                                      <span className="resume-metric-label-grid">Experience:</span>
                                      <span className="resume-metric-value-grid">{analysis.experienceMatch}%</span>
                                    </div>
                                    <div className="resume-metric-grid">
                                      <span className="resume-metric-label-grid">Education:</span>
                                      <span className="resume-metric-value-grid">{analysis.educationMatch}%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Detailed Reasoning Modal - Rendered via Portal to document.body */}
                    {selectedResumeForDetails && isMounted && typeof document !== 'undefined' && document.body && createPortal(
                      <div 
                        className="resumes-modal-overlay detailed-reasoning-overlay"
                        onClick={(e) => {
                          if (e.target === e.currentTarget) {
                            setSelectedResumeForDetails(null)
                          }
                        }}
                      >
                        <div className="resumes-modal detailed-reasoning-modal">
                          <div className="resumes-modal-header">
                            <h3>Detailed Fit Analysis: {selectedResumeForDetails.resumeName || 'Resume'}</h3>
                            <button 
                              className="resumes-modal-close"
                              onClick={() => setSelectedResumeForDetails(null)}
                              aria-label="Close"
                              title="Close"
                            >
                              ✕
                            </button>
                          </div>
                          <div className="resumes-modal-content detailed-reasoning-content">
                            {/* Overall Score and Reasoning */}
                            <div className="reasoning-section">
                              <div className="reasoning-section-header">
                                <span className="reasoning-section-title">Overall Fit Score</span>
                                <div className={`fit-score-badge-large ${getFitScoreColor(selectedResumeForDetails.fitScore)}`}>
                                  {selectedResumeForDetails.fitScore}%
                                </div>
                              </div>
                              {selectedResumeForDetails.overallReasoning && selectedResumeForDetails.overallReasoning.trim() ? (
                                <div className="reasoning-text">{selectedResumeForDetails.overallReasoning}</div>
                              ) : null}
                            </div>

                            {/* Skills Match */}
                            <div className="reasoning-section">
                              <div className="reasoning-section-header">
                                <span className="reasoning-section-title">Skills Match</span>
                                <span className="reasoning-section-score">{selectedResumeForDetails.skillsMatch}%</span>
                              </div>
                              {selectedResumeForDetails.skillsReasoning && selectedResumeForDetails.skillsReasoning.trim() ? (
                                <div className="reasoning-text">{selectedResumeForDetails.skillsReasoning}</div>
                              ) : (
                                <div className="reasoning-text-empty">
                                  <p>No detailed reasoning available for skills match.</p>
                                  <p className="text-sm mt-2 text-gray-500">
                                    This may be because the fit analysis was created before detailed reasoning was added. 
                                    Try editing and saving the job application to trigger a new analysis with reasoning.
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Experience Match */}
                            <div className="reasoning-section">
                              <div className="reasoning-section-header">
                                <span className="reasoning-section-title">Experience Match</span>
                                <span className="reasoning-section-score">{selectedResumeForDetails.experienceMatch}%</span>
                              </div>
                              {selectedResumeForDetails.experienceReasoning && selectedResumeForDetails.experienceReasoning.trim() ? (
                                <div className="reasoning-text">{selectedResumeForDetails.experienceReasoning}</div>
                              ) : (
                                <div className="reasoning-text-empty">
                                  <p>No detailed reasoning available for experience match.</p>
                                  <p className="text-sm mt-2 text-gray-500">
                                    This may be because the fit analysis was created before detailed reasoning was added. 
                                    Try editing and saving the job application to trigger a new analysis with reasoning.
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Education Match */}
                            <div className="reasoning-section">
                              <div className="reasoning-section-header">
                                <span className="reasoning-section-title">Education Match</span>
                                <span className="reasoning-section-score">{selectedResumeForDetails.educationMatch}%</span>
                              </div>
                              {selectedResumeForDetails.educationReasoning && selectedResumeForDetails.educationReasoning.trim() ? (
                                <div className="reasoning-text">{selectedResumeForDetails.educationReasoning}</div>
                              ) : (
                                <div className="reasoning-text-empty">
                                  <p>No detailed reasoning available for education match.</p>
                                  <p className="text-sm mt-2 text-gray-500">
                                    This may be because the fit analysis was created before detailed reasoning was added. 
                                    Try editing and saving the job application to trigger a new analysis with reasoning.
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Strengths */}
                            {selectedResumeForDetails.strengths && selectedResumeForDetails.strengths.length > 0 && (
                              <div className="reasoning-section">
                                <div className="reasoning-section-header">
                                  <span className="reasoning-section-title">Key Strengths</span>
                                </div>
                                <ul className="reasoning-list">
                                  {selectedResumeForDetails.strengths.map((strength: string, idx: number) => (
                                    <li key={`strength-${idx}`}>{strength}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Gaps */}
                            {selectedResumeForDetails.gaps && selectedResumeForDetails.gaps.length > 0 && (
                              <div className="reasoning-section">
                                <div className="reasoning-section-header">
                                  <span className="reasoning-section-title">Areas for Improvement</span>
                                </div>
                                <ul className="reasoning-list">
                                  {selectedResumeForDetails.gaps.map((gap: string, idx: number) => (
                                    <li key={`gap-${idx}`}>{gap}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Recommendations */}
                            {selectedResumeForDetails.recommendations && selectedResumeForDetails.recommendations.length > 0 && (
                              <div className="reasoning-section">
                                <div className="reasoning-section-header">
                                  <span className="reasoning-section-title">Recommendations</span>
                                </div>
                                <ul className="reasoning-list">
                                  {selectedResumeForDetails.recommendations.map((rec: string, idx: number) => (
                                    <li key={`rec-${idx}`}>{rec}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>,
                      document.body
                    )}
                  </>
                )
                    }
                    if (fitAnalysis?.noResumesMessage) {
                      return (
                        <div className="fit-score-no-resumes">
                          <div className="fit-score-no-resumes-content">
                            <div className="fit-score-no-resumes-icon">📄</div>
                            <div className="fit-score-no-resumes-text">
                              <p className="fit-score-no-resumes-title">{fitAnalysis.noResumesMessage}</p>
                              <p className="fit-score-no-resumes-action">
                                Upload and process resumes to get detailed job fit analysis. 
                                Go to the <strong>Resumes</strong> page to upload your resumes and wait for ATS processing to complete.
                              </p>
                              {jobData.id && onReprocessFitAnalysis && (
                                <button
                                  className={`reprocess-fit-analysis-button ${isReprocessingFitAnalysis || isLoadingFitAnalysis ? 'disabled' : ''}`}
                                  onClick={onReprocessFitAnalysis}
                                  disabled={isReprocessingFitAnalysis || isLoadingFitAnalysis}
                                  title="Reprocess resume fit analysis"
                                >
                                  {isReprocessingFitAnalysis || isLoadingFitAnalysis ? 'Processing...' : 'Reprocess Resume Fit Analysis'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    }
                    // Show reprocess button if no fit analysis but resumes are available (no noResumesMessage)
                    // and we have an application ID and reprocess handler
                    if (!fitAnalysis?.resumeFitAnalyses?.length && !fitAnalysis?.noResumesMessage && jobData.id && onReprocessFitAnalysis) {
                      return (
                        <div className="fit-score-no-data">
                          <div className="fit-score-no-resumes-content">
                            <div className="fit-score-no-resumes-icon">🔄</div>
                            <div className="fit-score-no-resumes-text">
                              <p className="fit-score-no-resumes-title">Resume fit analysis not available</p>
                              <p className="fit-score-no-resumes-action">
                                Click the button below to analyze your resumes against this job position.
                              </p>
                              <button
                                className={`reprocess-fit-analysis-button ${isReprocessingFitAnalysis || isLoadingFitAnalysis ? 'disabled' : ''}`}
                                onClick={onReprocessFitAnalysis}
                                disabled={isReprocessingFitAnalysis || isLoadingFitAnalysis}
                                title="Reprocess resume fit analysis"
                              >
                                {isReprocessingFitAnalysis || isLoadingFitAnalysis ? 'Processing...' : 'Reprocess Resume Fit Analysis'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div className="fit-score-no-data">
                        <div className="fit-score-no-resumes-content">
                          <div className="fit-score-no-resumes-icon">⏳</div>
                          <div className="fit-score-no-resumes-text">
                            <p className="fit-score-no-resumes-title">Resume fit analysis is processing...</p>
                            <p className="fit-score-no-resumes-action">
                              The system is analyzing your resumes against this job. This usually takes about 30-60 seconds.
                              Make sure you have uploaded and processed resumes with ATS data.
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assigned Resume Viewer - Hidden in edit mode */}
        {!hideResumeSelection && jobData.id && (
          <div className="section-group-horizontal assigned-resume-section">
            <AssignedResumeViewer
              jobApplicationId={jobData.id}
              selectedResumeId={jobData.selectedResumeId || null}
              resumeSelectionType={jobData.resumeSelectionType || null}
              availableResumes={availableResumes}
              tailoredResume={tailoredResume}
              fitAnalysis={fitAnalysis}
              onEdit={() => setIsResumeSelectionSidebarOpen(true)}
            />
            {jobData.id && onResumeSelectionChange && (
              <div className="resume-selection-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setIsResumeSelectionSidebarOpen(true)}
                  title={jobData.resumeSelectionType ? 'Change resume selection' : 'Select a resume'}
                >
                  {jobData.resumeSelectionType ? 'Change Resume' : 'Select Resume'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Resume Selection Sidebar - Hidden in edit mode */}
        {!hideResumeSelection && jobData.id && onResumeSelectionChange && (
          <ResumeSelectionSidebar
            isOpen={isResumeSelectionSidebarOpen}
            onClose={() => setIsResumeSelectionSidebarOpen(false)}
            jobApplicationId={jobData.id}
            fitAnalysis={fitAnalysis}
            availableResumes={availableResumes}
            tailoredResume={tailoredResume}
            selectedResumeId={jobData.selectedResumeId || null}
            resumeSelectionType={jobData.resumeSelectionType || null}
            onResumeSelectionChange={onResumeSelectionChange}
            onTailoredResumeUpdated={onTailoredResumeUpdated || (() => {})}
            onSelectionSaved={() => {
              // Refresh the job application data to show updated resume selection
              if (onTailoredResumeUpdated) {
                onTailoredResumeUpdated()
              }
            }}
          />
        )}

        {/* Company Intelligence Details Modal */}
        {showCompanyIntelligenceDetails && companyIntelligence?.status === 'ok' && isMounted && typeof document !== 'undefined' && document.body && createPortal(
          <div 
            className="resumes-modal-overlay detailed-reasoning-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCompanyIntelligenceDetails(false)
              }
            }}
          >
            <div 
              className="resumes-modal detailed-reasoning-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="resumes-modal-header">
                <h3>Detailed Company Intelligence: {companyIntelligence.resolved_company?.domain || jobData.company || 'Company'}</h3>
                <button 
                  className="resumes-modal-close"
                  onClick={() => setShowCompanyIntelligenceDetails(false)}
                  aria-label="Close"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="resumes-modal-content detailed-reasoning-content">
                {/* Overall Score */}
                {companyIntelligence.overall_score !== undefined && (() => {
                  const getCompanyScoreColor = (score: number): string => {
                    if (score >= 70) return 'excellent'
                    if (score >= 50) return 'good'
                    if (score >= 40) return 'moderate'
                    return 'low'
                  }
                  
                  return (
                    <div className="reasoning-section">
                      <div className="reasoning-section-header">
                        <span className="reasoning-section-title">Overall Company Score</span>
                        <div className={`fit-score-badge-large ${getCompanyScoreColor(companyIntelligence.overall_score)}`}>
                          {companyIntelligence.overall_score}
                        </div>
                      </div>
                      {companyIntelligence.resolved_company && (
                        <div className="mt-2 text-sm text-slate-500">
                          Domain: {companyIntelligence.resolved_company.domain} ({(companyIntelligence.resolved_company.confidence * 100).toFixed(0)}% confidence)
                        </div>
                      )}
                    </div>
                  )
                })()}

                {(companyIntelligence.coverage?.partial_failure_categories?.length || companyIntelligence.coverage?.empty_result_categories?.length) && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">Coverage</span>
                    </div>
                    <div className="reasoning-text">
                      {companyIntelligence.coverage?.partial_failure_categories && companyIntelligence.coverage.partial_failure_categories.length > 0 && (
                        <p><strong>Failed categories:</strong> {companyIntelligence.coverage.partial_failure_categories.join(', ')}</p>
                      )}
                      {companyIntelligence.coverage?.empty_result_categories && companyIntelligence.coverage.empty_result_categories.length > 0 && (
                        <p className="mt-2"><strong>No results:</strong> {companyIntelligence.coverage.empty_result_categories.join(', ')}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Layoffs Category */}
                {companyIntelligence.categories?.layoffs && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">📉 Layoffs / Restructuring (18mo)</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.layoffs.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.layoffs.summary}</p>
                      {companyIntelligence.categories.layoffs.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.layoffs.explanation}</p>
                      )}
                      {companyIntelligence.categories.layoffs.sources && companyIntelligence.categories.layoffs.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.layoffs.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hirings Category */}
                {companyIntelligence.categories?.hirings && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">📈 Hirings / Expansion (6mo)</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.hirings.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.hirings.summary}</p>
                      {companyIntelligence.categories.hirings.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.hirings.explanation}</p>
                      )}
                      {companyIntelligence.categories.hirings.sources && companyIntelligence.categories.hirings.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.hirings.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scandals Category */}
                {companyIntelligence.categories?.scandals && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">⚖️ Scandals / Legal Issues</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.scandals.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.scandals.summary}</p>
                      {companyIntelligence.categories.scandals.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.scandals.explanation}</p>
                      )}
                      {companyIntelligence.categories.scandals.sources && companyIntelligence.categories.scandals.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.scandals.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Management Category */}
                {companyIntelligence.categories?.management && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">👔 Management Changes</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.management.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.management.summary}</p>
                      {companyIntelligence.categories.management.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.management.explanation}</p>
                      )}
                      {companyIntelligence.categories.management.sources && companyIntelligence.categories.management.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.management.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Employee Reviews Category */}
                {companyIntelligence.categories?.reviews_employ && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">💼 Employee Reviews</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.reviews_employ.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.reviews_employ.summary}</p>
                      {companyIntelligence.categories.reviews_employ.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.reviews_employ.explanation}</p>
                      )}
                      {companyIntelligence.categories.reviews_employ.sources && companyIntelligence.categories.reviews_employ.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.reviews_employ.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer Reviews Category */}
                {companyIntelligence.categories?.reviews_customer && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">⭐ Customer Reviews</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.reviews_customer.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.reviews_customer.summary}</p>
                      {companyIntelligence.categories.reviews_customer.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.reviews_customer.explanation}</p>
                      )}
                      {companyIntelligence.categories.reviews_customer.sources && companyIntelligence.categories.reviews_customer.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.reviews_customer.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Company Size & Growth Category */}
                {companyIntelligence.categories?.company_size_growth && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">📊 Company Size & Growth</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.company_size_growth.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.company_size_growth.summary}</p>
                      {companyIntelligence.categories.company_size_growth.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.company_size_growth.explanation}</p>
                      )}
                      {companyIntelligence.categories.company_size_growth.sources && companyIntelligence.categories.company_size_growth.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.company_size_growth.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Diversity & ESG Category */}
                {companyIntelligence.categories?.diversity_esg && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">🌿 Diversity & ESG</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.diversity_esg.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.diversity_esg.summary}</p>
                      {companyIntelligence.categories.diversity_esg.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.diversity_esg.explanation}</p>
                      )}
                      {companyIntelligence.categories.diversity_esg.sources && companyIntelligence.categories.diversity_esg.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.diversity_esg.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Culture & Benefits Category */}
                {companyIntelligence.categories?.culture_benefits && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">🎁 Culture & Benefits</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.culture_benefits.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.culture_benefits.summary}</p>
                      {companyIntelligence.categories.culture_benefits.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.culture_benefits.explanation}</p>
                      )}
                      {companyIntelligence.categories.culture_benefits.sources && companyIntelligence.categories.culture_benefits.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.culture_benefits.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Competitors & Market Category */}
                {companyIntelligence.categories?.competitors_market && (
                  <div className="reasoning-section">
                    <div className="reasoning-section-header">
                      <span className="reasoning-section-title">🏁 Competitors & Market</span>
                      <span className="reasoning-section-score">{companyIntelligence.categories.competitors_market.score}</span>
                    </div>
                    <div className="reasoning-text">
                      <p><strong>Summary:</strong> {companyIntelligence.categories.competitors_market.summary}</p>
                      {companyIntelligence.categories.competitors_market.explanation && (
                        <p className="mt-2"><strong>Details:</strong> {companyIntelligence.categories.competitors_market.explanation}</p>
                      )}
                      {companyIntelligence.categories.competitors_market.sources && companyIntelligence.categories.competitors_market.sources.length > 0 && (
                        <div className="mt-4">
                          <strong>Sources:</strong>
                          <ul className="reasoning-list">
                            {companyIntelligence.categories.competitors_market.sources.map((source: any, idx: number) => (
                              <li key={idx}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  {source.title || source.url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Section 5: Job Summary - Only show if has data */}
        {hasValue(jobData.jobSummary) && (
          <div className="section-group-horizontal">
            <div className="fields-grid-compact">
              <EditableField
                field="jobSummary"
                value={jobData.jobSummary}
                label="Job Summary"
                icon="📝"
                type="textarea"
              />
            </div>
          </div>
        )}

        {/* Section 6: Technologies & Tools - Always show in edit mode */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="technologies"
              value={jobData.technologies}
              label="Technologies & Tools"
              icon="⚙️"
              hideIfEmpty={false}
              fullWidth={true}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 7: Education & Skills - Always show in edit mode */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="education"
              value={jobData.education}
              label="Education Requirements"
              icon="🎓"
              hideIfEmpty={false}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 8: Responsibilities - Always show in edit mode */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="responsibilities"
              value={jobData.responsibilities}
              label="Key Responsibilities"
              icon="✅"
              hideIfEmpty={false}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 9: Required Qualifications */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="requiredQualifications"
              value={jobData.requiredQualifications}
              label="Required Qualifications"
              icon="🔒"
              hideIfEmpty={false}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 10: Preferred Qualifications - Always show in edit mode */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="optionalQualifications"
              value={jobData.optionalQualifications}
              label="Preferred Qualifications"
              icon="⭐"
              hideIfEmpty={false}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 11: Benefits - Always show in edit mode */}
        <div className="section-group-horizontal">
          <div className="fields-grid-compact">
            <EditableField
              field="benefits"
              value={jobData.benefits}
              label="Benefits & Perks"
              icon="🎁"
              hideIfEmpty={false}
              readOnly={true}
            />
          </div>
        </div>

        {/* Section 12: Contacts */}
        {jobData.id && (
          <div className="section-group-horizontal">
            <ContactsSection
              contacts={jobData.contacts || []}
              jobApplicationId={jobData.id}
              onContactsChange={() => {
                // #region agent log CONTACTS_CHANGE_TRIGGERED
                sendDebugLog({
                  sessionId: 'debug-session',
                  runId: 'run5',
                  hypothesisId: 'N',
                  location: 'ComprehensiveJobDisplay.tsx:1942',
                  message: 'onContactsChange triggered - will reload page',
                  data: {
                    jobDataId: jobData.id,
                    callStack: new Error().stack?.substring(0, 500),
                  },
                  timestamp: Date.now(),
                });
                // #endregion
                // Trigger a refresh by calling onDataChange with updated contacts
                // The parent component should reload the application data
                if (jobData.id) {
                  // Use a callback to refresh - parent component should handle this
                  // For now, we'll update the local state by fetching fresh data
                  // This is a temporary solution - parent should provide a refresh callback
                  setTimeout(() => {
                    // #region agent log RELOAD_CALLED
                    sendDebugLog({
                      sessionId: 'debug-session',
                      runId: 'run5',
                      hypothesisId: 'O',
                      location: 'ComprehensiveJobDisplay.tsx:1968',
                      message: 'About to call window.location.reload()',
                      data: {
                        jobDataId: jobData.id,
                        callStack: new Error().stack?.substring(0, 500),
                      },
                      timestamp: Date.now(),
                    });
                    // #endregion
                    window.location.reload()
                  }, 500)
                }
              }}
              viewMode={viewMode}
            />
          </div>
        )}

        {/* Note: Interview prep is now accessed separately via the Interview Preparation button (👨‍🏫) in ApplicationsGrid */}
      </div>
    </div>
  )
}
